/**
 * @module LibraryPanel
 * @purpose Compact side-list of library hypotheses that did NOT earn a workflow card —
 *   each shown with a factual indicator-coverage ratio and neutral status. Lets a large
 *   library exist without flooding the analyst's attention with unmatched patterns.
 * @depends LibraryPanelRow, types/reasoning, react-i18next
 * @dependedOnBy HypothesesView
 *
 * @rules
 * - Performance: pure display — all data via props, no data fetching
 * - State: none
 * - INVARIANT: Evidenx does not score — the panel presents an inventory, not a ranking.
 */

import { useTranslation } from 'react-i18next'
import type { Hypothesis } from '../../../../../types/reasoning'
import { LibraryPanelRow } from './LibraryPanelRow'

interface LibraryPanelProps {
  hypotheses: Hypothesis[]
}

const PANEL_STYLE: React.CSSProperties = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  overflow: 'hidden',
}

const HEADER_STYLE: React.CSSProperties = {
  padding: '12px',
}

const TITLE_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: '0.85rem',
  fontWeight: 600,
  color: 'var(--text)',
}

const SUBTITLE_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.72rem',
  color: 'var(--text-muted)',
  marginTop: '4px',
}

const CAPTION_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.62rem',
  color: 'var(--text-muted)',
  fontStyle: 'italic',
  padding: '8px 12px',
  borderTop: '1px solid var(--border)',
}

/**
 * The hypothesis library side-list.
 *
 * @behaviour Renders one compact LibraryPanelRow per hypothesis. Returns nothing when the
 *   list is empty, so the panel only appears when there are library-only hypotheses.
 * @errors n/a
 */
export function LibraryPanel({ hypotheses }: LibraryPanelProps): React.ReactElement | null {
  const { t } = useTranslation('analysis')
  if (hypotheses.length === 0) {
    return null
  }
  return (
    <section style={PANEL_STYLE} aria-label={t('reasoning.hypothesis.library.title')}>
      <div style={HEADER_STYLE}>
        <div style={TITLE_STYLE}>{t('reasoning.hypothesis.library.title')}</div>
        <div style={SUBTITLE_STYLE}>{t('reasoning.hypothesis.library.subtitle')}</div>
      </div>
      {hypotheses.map((hypothesis) => (
        <LibraryPanelRow key={hypothesis.hypothesisId} hypothesis={hypothesis} />
      ))}
      <div style={CAPTION_STYLE} role="note">
        {t('reasoning.hypothesis.library.coverageCaption')}
      </div>
    </section>
  )
}
